/**
 * Toasts : le seul canal de feedback d'erreur, puisque le CLAUDE.md interdit
 * toute modale bloquante pendant le jeu.
 */
import { useEffect, type ReactElement } from 'react';
import { useRoomStore, type ToastState } from '../store/roomStore.js';
import { useT } from '../locales/useT.js';

const TOAST_MS = 4000;
/**
 * Une bulle qui porte un LIEN vit deux fois plus longtemps.
 *
 * Quatre secondes suffisent a lire « Untel a rejoint la partie », pas a lire un
 * message, comprendre qu'il y a un lien, viser et cliquer. La duree doit suivre
 * ce qu'on demande au joueur de faire, pas la longueur du texte.
 */
const TOAST_WITH_LINK_MS = TOAST_MS * 2;

function Toast({ toast, onDismiss }: { toast: ToastState; onDismiss: () => void }): ReactElement {
  const t = useT();

  const hasLink = toast.link !== undefined;
  useEffect(() => {
    const timer = window.setTimeout(onDismiss, hasLink ? TOAST_WITH_LINK_MS : TOAST_MS);
    return () => window.clearTimeout(timer);
  }, [onDismiss, hasLink]);

  return (
    <div className="toast" data-tone={toast.tone} role="status">
      {t(toast.key, toast.params)}
      {toast.link !== undefined && (
        <a
          className="toast__link"
          href={toast.link.href}
          target="_blank"
          rel="noreferrer noopener"
        >
          {t(toast.link.key)} →
        </a>
      )}
    </div>
  );
}

export function ToastStack(): ReactElement {
  const toasts = useRoomStore((state) => state.toasts);
  const dismiss = useRoomStore((state) => state.dismissToast);

  return (
    <div className="toast-stack" aria-live="polite">
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} onDismiss={() => dismiss(toast.id)} />
      ))}
    </div>
  );
}
